import ky from "ky";
import { computed, ref } from "vue";
import getRemoteURL from "./get-remote-url";

interface UserSessionV1 {
  _id: string;
  // emailHash?: string;
  username: string;
  profile: {
    email?: string;
  };
  studyCount?: number;
  _flags?: {
    maxStudies: number;
  };
}
interface UserSessionV2 {
  objectType: "user";
  _id: string;
  // emailHash?: string;
  username: string;
  primaryEmail?: string;
  emails: {
    email: string;
    primary: boolean;
  }[];
  usageLimits: {
    caseDefCount: number;
    maxStudies: number;
  };
}

interface UserSession {
  _id: string;
  // emailHash?: string;
  email?: string;
  username?: string;
  studyCount?: number;
  maxStudies?: number;
}

const userSession = ref<UserSession | null>(null);

// Tracks whether the session request has resolved yet, so the UI can
// distinguish "still checking" from "confirmed logged out" and avoid
// flashing a not-logged-in warning before the request completes.
type SessionStatus = "unknown" | "authenticated" | "unauthenticated";
const sessionStatus = ref<SessionStatus>("unknown");

const pacsbinVersion = Number(import.meta.env.VITE_PACSBIN_VERSION);

const sessionURL =
  pacsbinVersion === 2
    ? getRemoteURL("/api/user?withUsage=true")
    : getRemoteURL("/user/getSession?withMeta=true");

async function getUserSession() {
  let session: UserSessionV1 | UserSessionV2;
  try {
    session = await ky
      .get(sessionURL, {
        credentials: "include",
      })
      .json();
  } catch (err) {
    // Request failed (e.g. 401) — the user is genuinely not logged in.
    userSession.value = null;
    sessionStatus.value = "unauthenticated";
    throw err;
  }
  if ("objectType" in session && session.objectType === "user") {
    const user = session;
    userSession.value = {
      _id: user._id,
      // emailHash: user.emailHash,
      email: user.primaryEmail || user.emails.find((e) => e.primary)?.email,
      username: user.username || user.emails.find((e) => e.primary)?.email,
      studyCount: user.usageLimits.caseDefCount,
      maxStudies: user.usageLimits.maxStudies,
    };
  } else {
    // userSession.value = {
    //   _id: session._id,
    //   // emailHash: session.emailHash,
    //   email: session.profile.email,
    //   username: session.username || session.profile.email,
    //   studyCount: session.studyCount,
    //   maxStudies: session._flags?.maxStudies,
    // };
  }
  sessionStatus.value = userSession.value?._id
    ? "authenticated"
    : "unauthenticated";
}

const isLoggedIn = computed(() => {
  return userSession.value?._id;
});

// Only true once we've confirmed the user is logged out — stays false while
// the session request is still pending so the warning doesn't flash on load.
const isLoggedOut = computed(() => {
  return sessionStatus.value === "unauthenticated";
});

/**
 * Decide if user has room in account for more studies
 */
const maxStudies = computed(() => {
  return userSession.value?.maxStudies;
});
const studyCount = computed(() => {
  // Allow overriding study count for development purposes
  const urlParams = new URLSearchParams(window.location.search);
  const studyCount = urlParams.get("studyCount")
    ? Number(urlParams.get("studyCount"))
    : userSession.value?.studyCount;

  return studyCount;
});
const hasRoomInAccount = computed(() => {
  if (maxStudies.value === -1) {
    // Unlimited
    return true;
  }
  if (maxStudies.value && studyCount.value !== undefined) {
    return maxStudies.value > studyCount.value;
  }
  return false;
});

export {
  userSession,
  getUserSession,
  isLoggedIn,
  isLoggedOut,
  sessionStatus,
  hasRoomInAccount,
  maxStudies,
  studyCount,
};
